import React, { useCallback, useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { canUseRounds } from "../../helpers/karkhanaEnabled";
import { getLoomChanges, getLoomHistory, listLooms, renameLoom, retireLoom } from "../../services/karkhanaApi";
import notifications from "../../common/Components/Notifications/Notification";
import ChangeList from "./sarees/ChangeList";
import { ChangeRow, cleanLoomCode } from "./sarees/sareeEditModel";
import { askReason } from "./ui/dialog";
import "../gi-registry/gi-registry.scss";
import { fmtDay, rupees } from "./rates/rateModel";
import { gajGirah } from "./rounds/sareeLength";
import PageHeader from "./ui/PageHeader";
import "./ui/karkhana-history.scss";
import "./workers/karkhana-workers.scss";
import { useKT } from "./i18n/kt";
import { kp } from "./karkhanaPaths";
import { useKarkhanaTitle } from "./ui/addressState";

const readRole = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

const STATUS: Record<string, string> = { ACTIVE: "On the loom", COMPLETED: "Complete", STOPPED: "Stopped", CUT: "Cut" };

/** Every saree a loom has woven, newest first; tap one for its day-by-day history. */
const KarkhanaLoomHistoryPage = () => {
  const { t } = useKT();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const role = readRole();
  const [data, setData] = useState<any>(null);
  const [changes, setChanges] = useState<ChangeRow[]>([]);
  // The loom record (retired, previous codes) from the studio's loom list.
  const [record, setRecord] = useState<any>(null);
  // Renaming and retiring a loom: the studio owner only (the server checks it too).
  const owner = role === "ORGANIZATION_ADMIN";
  const [code, setCode] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    getLoomHistory(id)
      .then((res: any) => setData(res?.success ? res.data : { sarees: [] }))
      .catch(() => setData({ sarees: [] }));
    listLooms()
      .then((res: any) => setRecord(res?.success ? (res.data || []).find((l: any) => l.id === id) || null : null))
      .catch(() => setRecord(null));
    getLoomChanges(id)
      .then((res: any) => setChanges(res?.success ? res.data || [] : []))
      .catch(() => setChanges([]));
  }, [id]);

  useEffect(() => {
    if (!canUseRounds(role)) {
      history.replace(kp.home());
      return;
    }
    load();
  }, [role, history, load]);

  const act = async (fn: () => Promise<any>, ok: string) => {
    setBusy(true);
    try {
      const res: any = await fn();
      if (!res?.success) {
        notifications({ title: t("Not saved"), message: t(res?.message || "Could not save"), type: "danger" });
        return false;
      }
      notifications({ title: t("Saved"), message: ok, type: "success" });
      load();
      return true;
    } catch {
      notifications({ title: t("No connection"), message: t("Nothing was saved. Try again."), type: "warning" });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveRename = async () => {
    if (!code) return;
    if (await act(() => renameLoom(id, code, note.trim() || undefined), t("Loom {{old}} is now {{code}}", { old: data?.loom?.code, code }))) {
      setCode(null);
      setNote("");
    }
  };

  const retire = async () => {
    const why = await askReason({
      title: t("Retire loom {{code}}? It disappears from the rounds and pickers; its sarees and history stay.", { code: data?.loom?.code }),
      ok: t("Retire loom"),
      placeholder: t("Why is it retired?"),
    });
    if (why === null) return;
    await act(() => retireLoom(id, why.trim() || undefined), t("Loom {{code}} retired", { code: data?.loom?.code }));
  };

  const retired = !!record?.retired;
  const money = !!data?.showMoney;
  useKarkhanaTitle(data?.loom ? `Loom ${data.loom.code}` : null);
  return (
    <div className="gi-registry-page kw-page kh-history">
      <PageHeader title={data?.loom ? `${t("Loom")} ${data.loom.code}` : t("Loom")} back={kp.rounds()} backLabel={t("Back")} />
      {!data ? <div className="kw-empty">{t("Loading…")}</div> : null}
      {retired ? <p className="kw-chip">{t("Retired: not shown in the rounds or pickers")}</p> : null}
      {data?.loom && owner ? (
        code === null ? (
          <div className="kh-since__actions" style={{ justifyContent: "flex-start", marginTop: 0, marginBottom: 13 }}>
            <button type="button" className="kh-btn" disabled={busy} onClick={() => setCode(data.loom.code)}>
              {t("Rename loom")}
            </button>
            {!retired ? (
              <button type="button" className="kh-btn" disabled={busy} onClick={retire}>
                {t("Retire loom")}
              </button>
            ) : null}
          </div>
        ) : (
          <div className="kh-since">
            <label className="kr-label" htmlFor="kh-loom-code">
              {t("New loom number")}
            </label>
            <input id="kh-loom-code" className="kr-select" value={code} autoFocus onChange={(e) => setCode(cleanLoomCode(e.target.value))} />
            <p className="kw-muted kh-since__hint">{t("Old entries keep showing, and searching the old number still finds this loom.")}</p>
            <label className="kr-label" htmlFor="kh-loom-note">
              {t("Why (optional)")}
            </label>
            <input id="kh-loom-note" className="kr-select" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} />
            <div className="kh-since__actions">
              <button type="button" className="kh-btn" disabled={busy} onClick={() => { setCode(null); setNote(""); }}>
                {t("Cancel")}
              </button>
              <button type="button" className="kh-btn is-primary" disabled={busy || !code || code === data.loom.code} onClick={saveRename}>
                {busy ? t("Saving…") : t("Save loom number")}
              </button>
            </div>
          </div>
        )
      ) : null}
      {data && !data.sarees?.length ? <div className="kw-empty">{t("No saree on this loom yet.")}</div> : null}
      <div className="kh-list">
        {(data?.sarees || []).map((s: any) => (
          <Link key={s.id} to={kp.saree(s.id)} className="kh-tile kh-saree-tile">
            <div className="kh-saree-tile__top">
              <strong>
                <span className="kw-code">{s.sareeNumber}</span> · {s.designNumber}
              </strong>
              <span className={`kw-chip${s.status === "ACTIVE" ? " is-info" : ""}`}>{t(STATUS[s.status] || s.status)}</span>
            </div>
            <span>
              {fmtDay(s.startedOn)}
              {s.finishedOn ? ` – ${fmtDay(s.finishedOn)}` : ""} · {s.days} {t("days")} · {gajGirah(Number(s.sareeInches || 0))} ({s.sareeInches} in)
            </span>
            <span>
              {t("Weavers")}: {(s.weavers || []).join(", ") || "—"}
              {money ? ` · ${t("weaving")} ${rupees(s.weavingPay, 0)}` : ""}
            </span>
          </Link>
        ))}
      </div>
      {changes.length ? (
        <>
          <h3 className="kr-group-title">{t("Changes")}</h3>
          <ChangeList changes={changes} />
        </>
      ) : null}
    </div>
  );
};

export default KarkhanaLoomHistoryPage;
